import React, { useState, useEffect, useContext } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  FormControl,
} from "@mui/material";
import Select from "react-select";
import EventRegistration from "@components/EventCamera/EventRegistration";
import { createEmployee, getCoordinadores } from "@services/employeeService";
import SuccessMessageModal from "@components/Reusable/SuccessMessageModal";
import WarningMessageModal from "@components/Reusable/WarningMessageModal";
import { AuthContext } from "@context/AuthContext";
import { getPlacesByUserNameParam } from "@services/userService";

// initialData (opcional): pre-llenar campos al crear desde un huérfano de cámara
// ({ cedula, first_name, last_name, photo, place_id }). El resto se completa a mano.
// onCreated (opcional): hook async post-alta (p.ej. actualizar al huérfano en la
// cámara con la cédula/nombre corregidos). Se espera ANTES de mostrar el éxito;
// puede devolver un texto que se agrega al mensaje. Si falla NO deshace el alta.
const CreateEmployeeModal = ({ isOpen, onClose, initialData = null, onCreated = null }) => {
  const selectedPlaceId = sessionStorage.getItem("selectedPlaceId");
  const [newEmployee, setNewEmployee] = useState({
    first_name: "",
    second_name: "",
    last_name: "",
    second_last_name: "",
    cedula: "",
    photo: "",
    phone: "",
    coordinador: "",
    place_id: selectedPlaceId ? selectedPlaceId : "",
    ...(initialData || {}),
  });

  useEffect(() => {
    if (isOpen && initialData) {
      setNewEmployee((prev) => ({ ...prev, ...initialData }));
    }
  }, [isOpen]);
  const [places, setPlaces] = useState([]);
  const [coordinadores, setCoordinadores] = useState([]); // sugerencias del autocomplete
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Empleado registrado exitosamente.");
  const [warningMessageVisible, setWarningMessageVisible] = useState(false);
  const { isAdminUser } = useContext(AuthContext);
  const USERNAME = sessionStorage.getItem("username");

  useEffect(() => {
    if (isOpen) {
      loadPlaces();
      getCoordinadores()
        .then((res) => setCoordinadores(Array.isArray(res.data) ? res.data : []))
        .catch(() => setCoordinadores([]));
    }
  }, [isOpen]);

  const loadPlaces = async () => {
    if (!selectedPlaceId) {
      try {
        const response = await getPlacesByUserNameParam(USERNAME);
        setPlaces(
          response.data[0].places.map((place) => ({
            label: place.name_place,
            value: place.id,
          }))
        );
      } catch (error) {
        console.error("Error loading places", error);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoCapture = (employee) => {
    setNewEmployee(employee);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (
      !newEmployee.first_name ||
      !newEmployee.last_name ||
      !newEmployee.cedula ||
      !newEmployee.phone ||
      !newEmployee.photo ||
      !newEmployee.place_id
    ) {
      setErrorMessage("Todos los campos con (*) son obligatorios.");
      setWarningMessageVisible(true);
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const res = await createEmployee(newEmployee);
      const cs = res?.data?.camera_sync;
      let msg = "Empleado registrado exitosamente.";
      if (cs && cs.enabled && cs.total > 0) {
        const parts = [`${cs.ok}/${cs.total} cámara(s) OK`];
        if (cs.partial) parts.push(`${cs.partial} sin imagen`);
        if (cs.failed) parts.push(`${cs.failed} con error`);
        msg += ` Sincronización: ${parts.join(", ")}.`;
        const bad = (cs.per_camera || []).filter((x) => x.status !== "ok");
        if (bad.length) {
          msg += ` Cámara(s) con problema: ${bad.map((x) => x.camera).join(", ")}.`;
          msg += " Podés reintentar desde el estado de sincronización de la cámara.";
        }
      } else if (cs && cs.enabled && cs.total === 0) {
        msg += " (La sede no tiene cámaras activas.)";
      }
      if (onCreated) {
        try {
          const extra = await onCreated(newEmployee, res);
          if (extra) msg += ` ${extra}`;
        } catch (e) {
          msg += ` Aviso: no se pudo actualizar la persona en la cámara (${
            e?.response?.data?.detail || "error"
          }); podés corregirla con "Editar en cámara".`;
        }
      }
      setSuccessMessage(msg);
      setIsLoading(false);
      setSuccessMessageVisible(true);
      setNewEmployee({
        first_name: "",
        second_name: "",
        last_name: "",
        second_last_name: "",
        cedula: "",
        photo: "",
        phone: "",
        coordinador: "",
        place_id: "",
      });
    } catch (error) {
      // 422 = la cámara rechazó la foto (sin rostro detectable): mostrar el motivo real.
      setErrorMessage(error?.response?.data?.detail || "Error al registrar el empleado.");
      setWarningMessageVisible(true);
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Crear Nuevo Empleado</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Primer Nombre *"
                  name="first_name"
                  value={newEmployee.first_name}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Segundo Nombre"
                  name="second_name"
                  value={newEmployee.second_name}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Primer Apellido *"
                  name="last_name"
                  value={newEmployee.last_name}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Segundo Apellido"
                  name="second_last_name"
                  value={newEmployee.second_last_name}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Documento de Identificación *"
                  name="cedula"
                  type="number"
                  value={newEmployee.cedula}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Celular *"
                  name="phone"
                  type="number"
                  value={newEmployee.phone}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={coordinadores}
                  value={newEmployee.coordinador || ""}
                  // freeSolo: escribir uno nuevo (onInputChange) o elegir de la lista.
                  onInputChange={(e, val) =>
                    setNewEmployee((prev) => ({ ...prev, coordinador: val }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Coordinador de área (opcional)" fullWidth />
                  )}
                />
              </Grid>
              {!selectedPlaceId && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Select
                      placeholder="Sede *"
                      id="place_id"
                      name="place_id"
                      value={places.find(
                        (place) => place.value === newEmployee.place_id
                      )}
                      options={places}
                      onChange={(selectedOption) => {
                        setNewEmployee((prev) => ({
                          ...prev,
                          place_id: selectedOption.value,
                        }));
                      }}
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle1">Tomar o Adjuntar Foto *</Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    width: "100%",
                    padding: 2,
                  }}
                >
                  <EventRegistration
                    data={newEmployee}
                    onPhotoTaken={handlePhotoCapture}
                  />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    O adjuntar una imagen desde tu dispositivo:
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{
                      mt: 1,
                      backgroundColor: "#0A2139",
                      color: "#f1f1f1",
                      "&:hover": {
                        backgroundColor: "#0A2139",
                      },
                    }}
                  >
                    Seleccionar Imagen
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setNewEmployee((prev) => ({
                              ...prev,
                              photo: reader.result,
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Button>
                  {newEmployee.photo && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">Vista previa:</Typography>
                      <img
                        src={newEmployee.photo}
                        alt="Foto del empleado"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "200px",
                          marginTop: 8,
                          borderRadius: "8px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ color: "#1d1d1b" }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          style={{ backgroundColor: "#0A2139", color: "#f1f1f1" }}
          disabled={isLoading}
          variant="contained"
        >
          Guardar
        </Button>
      </DialogActions>

      <SuccessMessageModal
        message={successMessage}
        isOpen={successMessageVisible}
        onClose={() => window.location.reload()}
      />
      <WarningMessageModal
        message={errorMessage}
        isOpen={warningMessageVisible}
        onClose={() => setWarningMessageVisible(false)}
      />
    </Dialog>
  );
};

export default CreateEmployeeModal;
